// src/pages/CompanyComparePage.jsx
// Side-by-side comparison of up to 3 companies.
// General tab: payment trend, top products, specialties, top providers.
// Ownership tab: holder counts, equity by year, holder type, interest types, top companies.
// Research tab: funding trend, product type split, top products, top investigators.

import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  getCompanyProfile,
  getOwnershipByCompanyId,
  getResearchByCompany,
  getResearchByCompanyByYear,
  getResearchProductSummaryByCompany,
  getOwnershipDetailByCompany,
  getResearchGeneralDualInterestCountByCompany,
  checkPaymentsByCompanyBatch,
  checkResearchByCompanyBatch,
  searchByCompany,
  formatCurrency,
} from "../utils/cmsApi";
import { useSubscription } from "../context/SubscriptionContext";

const CHART_COLORS = ["#f59e0b", "#6366f1", "#14b8a6"];
const ALL_YEARS = [2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025];

const TABS = [
  { id: "general", label: "General Payments", icon: "💊" },
  { id: "ownership", label: "Ownership", icon: "🏦" },
  { id: "research", label: "Research", icon: "🔬" },
];

function formatCompact(value) {
  if (!value || value === 0) return "—";
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

function exportCsv(rows, filename) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function ExportButton({ onClick, label = "↓ Export" }) {
  return (
    <button
      onClick={onClick}
      className="text-xs text-white/30 hover:text-white/60 transition-colors shrink-0"
    >
      {label}
    </button>
  );
}

function shortPaymentType(raw) {
  const map = {
    "Food and Beverage": "Food & Beverage",
    "Consulting Fee": "Consulting",
    "Travel and Lodging": "Travel & Lodging",
    "Education": "Education",
    "Royalty or License": "Royalties",
    "Compensation for services other than consulting, including serving as faculty or as a speaker at a venue other than a continuing education program": "Speaking Fees",
    "Compensation for serving as faculty or as a speaker at an unaccredited and noncertified continuing education program": "Faculty/Speaking",
    "Compensation for serving as faculty or as a speaker at a non-accredited and noncertified continuing education program": "Faculty/Speaking",
    "Compensation for serving as faculty or as a speaker for a non-accredited and noncertified continuing education program": "Faculty/Speaking",
    "Compensation for serving as faculty or as a speaker for a medical education program": "Faculty/Speaking (CME)",
    "Compensation for serving as faculty or as a speaker at an accredited or certified continuing education program": "Faculty/Speaking (CME)",
    "Compensation for serving as faculty or as a speaker for an accredited or certified continuing education program": "Faculty/Speaking (CME)",
    "Gift": "Gift",
    "Honoraria": "Honoraria",
    "Research": "Research",
    "Grant": "Grant",
    "Entertainment": "Entertainment",
    "Charitable Contribution": "Charity",
  };
  return map[raw] || raw;
}

function shortSpecialty(raw) {
  if (!raw) return "Other";
  const parts = raw.split("|");
  return parts[parts.length - 1].trim() || raw;
}

function categorizeTerms(terms) {
  if (!terms) return "Other";
  const t = terms.toUpperCase();
  if (t.includes("COMMON") && t.includes("PREFERRED")) return "Common & Preferred Stock";
  if (t.includes("RESTRICTED STOCK") || t.includes("RSU") || t.includes("PRSU")) return "Restricted Stock / RSUs";
  if (t.includes("STOCK OPTION") || t.includes("OPTION TO PURCHASE") || t.includes("OPTION GRANT") || (t.includes("OPTION") && t.includes("EXERCISE"))) return "Stock Options";
  if (t.includes("WARRANT")) return "Warrants";
  if (t.includes("COMMON STOCK") || t.includes("COMMON SHARE") || t.includes("FOUNDER") || t === "COMMON" || t.includes("STOCKHOLDER")) return "Common Stock";
  if (t.includes("PREFERRED") || t.includes("SERIES A") || t.includes("SERIES B") || t.includes("SERIES C") || t.includes("SERIES D") || t.includes("SERIES E") || t.includes("SERIES F") || t.includes("SERIES SEED")) return "Preferred Stock";
  if (t.includes("LLC") || t.includes("MEMBERSHIP INTEREST") || t.includes("MEMBERSHIP UNIT") || t.includes("UNIT HOLDER") || t.includes("UNITHOLDER")) return "LLC / Membership Interest";
  if (t.includes("CONVERTIBLE") || t.includes("SAFE") || t.includes("PROMISSORY NOTE")) return "Convertible Instruments";
  if (t.includes("PARTNERSHIP") || t.includes("CAPITAL CONTRIBUTION") || t.includes("CAPITAL INVESTMENT")) return "Partnership Interest";
  if (t.includes("ACQUISITION") || t.includes("LIQUIDAT") || t.includes("MERGER") || t.includes("DISSOLVED") || t.includes("BANKRUPTCY")) return "Acquisition / Liquidation";
  if (t.includes("ROYALT") || t.includes("DIVIDEND") || t.includes("DISTRIBUTION")) return "Royalties / Distributions";
  if (t.includes("EQUITY") || t.includes("INVESTMENT") || t === "STOCK" || t === "SHARES" || t.includes("OWNERSHIP INTEREST")) return "Equity Investment";
  return "Other";
}

// ── Ownership value SVG line chart ───────────────────────────────────────────
function OwnershipValueLineChart({ yearlyData, color, companyName, companyId }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);

  const points = ALL_YEARS.map((yr) => {
    const yd = yearlyData.find((d) => d.programYear === yr);
    return { year: yr, value: yd?.totalValue || 0 };
  });

  if (!points.some((p) => p.value > 0)) return null;

  const W = 260, H = 70;
  const PAD = { left: 8, right: 8, top: 16, bottom: 18 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;
  const maxVal = Math.max(...points.map((p) => p.value), 1);

  function xPos(i) { return PAD.left + (i / (points.length - 1 || 1)) * chartW; }
  function yPos(v) { return PAD.top + chartH - (v / maxVal) * chartH; }

  const pathD = points.map((p, i) => `${i === 0 ? "M" : "L"} ${xPos(i)} ${yPos(p.value)}`).join(" ");
  const areaD = `${pathD} L ${xPos(points.length - 1)} ${PAD.top + chartH} L ${xPos(0)} ${PAD.top + chartH} Z`;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-white/40 text-xs uppercase tracking-widest">Ownership value by year</p>
        <ExportButton onClick={() => exportCsv(
          yearlyData.filter((d) => d.totalValue > 0).map((d) => ({
            "Company": companyName,
            "Company ID": `'${companyId}`,
            "Year": d.programYear,
            "Total Value ($)": d.totalValue,
            "Holder Count": d.holderCount,
          })),
          `${companyName.replace(/\s+/g, "_")}_ownership_value_by_year.csv`
        )} />
      </div>
      <svg
        width="100%"
        viewBox={`0 0 ${W} ${H}`}
        style={{ overflow: "visible" }}
        onMouseLeave={() => setHoveredIdx(null)}
      >
        {/* Grid lines */}
        {[0, 0.5, 1].map((pct) => (
          <line
            key={pct}
            x1={PAD.left} y1={PAD.top + chartH * (1 - pct)}
            x2={PAD.left + chartW} y2={PAD.top + chartH * (1 - pct)}
            stroke="rgba(255,255,255,0.06)" strokeWidth="0.5"
          />
        ))}
        {/* Area fill */}
        <path d={areaD} fill={color} fillOpacity="0.08" />
        {/* Line */}
        <path d={pathD} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
        {/* Points + hover zones */}
        {points.map((p, i) => (
          <g key={i}>
            <rect
              x={xPos(i) - 10} y={PAD.top} width={20} height={chartH}
              fill="transparent"
              onMouseEnter={() => setHoveredIdx(i)}
            />
            {p.value > 0 && (
              <circle
                cx={xPos(i)} cy={yPos(p.value)} r={hoveredIdx === i ? 3 : 2}
                fill={color} stroke="#0f172a" strokeWidth="1"
              />
            )}
            {/* Tooltip */}
            {hoveredIdx === i && p.value > 0 && (
              <g>
                <rect
                  x={Math.min(xPos(i) - 28, W - 62)} y={yPos(p.value) - 30}
                  width={60} height={26} rx={4}
                  fill="#0f172a" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5"
                />
                <text
                  x={Math.min(xPos(i) - 28, W - 62) + 30} y={yPos(p.value) - 19}
                  textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize="7"
                >{p.year}</text>
                <text
                  x={Math.min(xPos(i) - 28, W - 62) + 30} y={yPos(p.value) - 9}
                  textAnchor="middle" fill={color} fontSize="8" fontWeight="600"
                >{formatCompact(p.value)}</text>
              </g>
            )}
          </g>
        ))}
        {/* X-axis year labels — always show full range */}
        <text x={xPos(0)} y={H - 2} textAnchor="middle" fill="rgba(255,255,255,0.2)" fontSize="7">2016</text>
        <text x={xPos(points.length - 1)} y={H - 2} textAnchor="middle" fill="rgba(255,255,255,0.2)" fontSize="7">2025</text>
      </svg>
    </div>
  );
}

// ── Shared mini bar chart with labels + hover tooltip ─────────────────────────
function MiniTrendBar({ data, color, height = 80, selectedYear = "all", formatValue }) {
  // formatValue: optional custom formatter. Defaults to dollar compact.
  const fmt = formatValue || formatCompact;
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const max = Math.max(...data.map((d) => d.value || 0), 1);

  return (
    <div className="relative">
      <div className="flex items-end gap-0.5" style={{ height, paddingTop: "18px" }}>
        {data.map((d, i) => {
          const pct = (d.value / max) * 100;
          const isSelected = selectedYear !== "all" && d.year === parseInt(selectedYear);
          const isHovered = hoveredIdx === i;
          return (
            <div
              key={i}
              className="flex-1 flex flex-col items-center justify-end h-full relative"
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
            >
              {/* Hover tooltip */}
              {isHovered && d.value > 0 && (
                <div
                  className="absolute pointer-events-none z-20 rounded-lg border border-white/15 px-2 py-1.5 text-xs"
                  style={{
                    bottom: `${Math.max(pct, 3)}%`,
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "#0f172a",
                    marginBottom: "4px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <p className="text-white/50" style={{ fontSize: "9px" }}>{d.year}</p>
                  <p className="font-mono" style={{ color, fontSize: "10px" }}>{fmt(d.value)}</p>
                </div>
              )}
              {/* Data label above bar */}
              {d.value > 0 && (
                <div
                  className="absolute w-full text-center pointer-events-none"
                  style={{ bottom: `${Math.max(pct, 3)}%`, paddingBottom: "1px" }}
                >
                  <span style={{ fontSize: "6px", color, opacity: isSelected || isHovered ? 1 : 0.7 }}>
                    {fmt(d.value)}
                  </span>
                </div>
              )}
              <div
                className="w-full rounded-t transition-all"
                style={{
                  height: d.value > 0 ? `${Math.max(pct, 3)}%` : "2px",
                  background: d.value > 0 ? color : "rgba(255,255,255,0.05)",
                  opacity: selectedYear === "all" ? 0.85 : isSelected ? 1 : 0.25,
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Payment type color palette (matches CompanyProfilePage) ──────────────────
const PAYMENT_TYPE_COLORS_MAP = {
  "Consulting":            "rgba(251,191,36,0.85)",
  "Food & Beverage":       "rgba(34,197,94,0.75)",
  "Travel & Lodging":      "rgba(59,130,246,0.75)",
  "Royalties":             "rgba(139,92,246,0.85)",
  "Speaking Fees":         "rgba(249,115,22,0.80)",
  "Education":             "rgba(20,184,166,0.75)",
  "Gift":                  "rgba(236,72,153,0.75)",
  "Research":              "rgba(14,165,233,0.75)",
  "Honoraria":             "rgba(245,158,11,0.75)",
  "Grant":                 "rgba(16,185,129,0.80)",
  "Charity":               "rgba(168,85,247,0.75)",
  "Faculty/Speaking":      "rgba(99,102,241,0.80)",
  "Faculty/Speaking (CME)":"rgba(147,51,234,0.75)",
  "Other":                 "rgba(100,116,139,0.70)",
};
function getPaymentTypeColor(label) {
  return PAYMENT_TYPE_COLORS_MAP[label] || PAYMENT_TYPE_COLORS_MAP["Other"];
}

// ── Payment type pie chart (top 4 + Other bucket) ────────────────────────────
function PaymentTypePieChart({ paymentTypes, color, focusYearLabel, focusYearData, company, isTypeFiltered, selectedPaymentType }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);

  // Build top 4 + Other
  const filtered = isTypeFiltered
    ? paymentTypes.filter(([label]) => selectedPaymentType.has(label))
    : paymentTypes;

  const sorted = [...filtered].sort((a, b) => b[1] - a[1]);
  const top4 = sorted.slice(0, 4);
  const rest = sorted.slice(4);
  const otherTotal = rest.reduce((s, [, v]) => s + v, 0);
  const segments = otherTotal > 0 ? [...top4, ["Other", otherTotal]] : top4;
  const total = segments.reduce((s, [, v]) => s + v, 0);
  if (!total) return null;

  // SVG donut
  const CX = 44, CY = 44, R = 36, INNER = 22;
  let angle = -90;
  const paths = segments.map(([label, value], i) => {
    const sweep = (value / total) * 360;
    const start = angle;
    angle += sweep;
    const end = angle;
    const toRad = (d) => (d * Math.PI) / 180;
    const x1 = CX + R * Math.cos(toRad(start));
    const y1 = CY + R * Math.sin(toRad(start));
    const x2 = CX + R * Math.cos(toRad(end - 0.5));
    const y2 = CY + R * Math.sin(toRad(end - 0.5));
    const ix1 = CX + INNER * Math.cos(toRad(start));
    const iy1 = CY + INNER * Math.sin(toRad(start));
    const ix2 = CX + INNER * Math.cos(toRad(end - 0.5));
    const iy2 = CY + INNER * Math.sin(toRad(end - 0.5));
    const large = sweep > 180 ? 1 : 0;
    return {
      label, value,
      d: `M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${INNER} ${INNER} 0 ${large} 0 ${ix1} ${iy1} Z`,
      fillColor: getPaymentTypeColor(label),
    };
  });

  const hov = hoveredIdx !== null ? segments[hoveredIdx] : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-white/40 text-xs uppercase tracking-widest">Payment types ({focusYearLabel})</p>
        <ExportButton onClick={() => {
          let allTypes = [];
          try {
            const raw = Array.isArray(focusYearData?.paymentTypes)
              ? focusYearData.paymentTypes
              : JSON.parse(focusYearData?.paymentTypes || "[]");
            const fullMap = {};
            raw.forEach((t) => {
              const label = shortPaymentType(t.payment_type || t.paymentType || "");
              if (label) fullMap[label] = (fullMap[label] || 0) + (t.amount || 0);
            });
            allTypes = Object.entries(fullMap).sort((a, b) => b[1] - a[1]);
          } catch {}
          exportCsv(
            allTypes.map(([label, amount]) => ({
              "Company": company.companyName,
              "Company ID": `'${company.companyId}`,
              "Year": focusYearLabel,
              "Payment Type": label,
              "Amount ($)": amount,
            })),
            `${company.companyName.replace(/\s+/g, "_")}_payment_types_${focusYearLabel}.csv`
          );
        }} />
      </div>
      <div className="flex items-center gap-3">
        {/* Donut */}
        <div className="shrink-0">
          <svg width="88" height="88" viewBox="0 0 88 88" onMouseLeave={() => setHoveredIdx(null)}>
            {paths.map((p, i) => (
              <path
                key={p.label}
                d={p.d}
                fill={p.fillColor}
                opacity={hoveredIdx === null || hoveredIdx === i ? 1 : 0.3}
                stroke="#0f172a"
                strokeWidth="1"
                style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                onMouseEnter={() => setHoveredIdx(i)}
              />
            ))}
            {/* Center label */}
            <text x="44" y="41" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize="8" fontWeight="600">
              {hov ? formatCompact(hov[1]) : formatCompact(total)}
            </text>
            <text x="44" y="51" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="6">
              {hov ? (hov[0].length > 9 ? hov[0].slice(0, 9) + "…" : hov[0]) : "total"}
            </text>
          </svg>
        </div>
        {/* Legend */}
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          {segments.map(([label, value], i) => {
            const pct = ((value / total) * 100).toFixed(0);
            return (
              <div
                key={label}
                className="flex items-center justify-between gap-1.5 cursor-pointer"
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
                style={{ opacity: hoveredIdx === null || hoveredIdx === i ? 1 : 0.35, transition: "opacity 0.15s" }}
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <div className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: getPaymentTypeColor(label) }} />
                  <span className="text-white/50 text-xs truncate">{label}</span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-white/25 text-xs">{pct}%</span>
                  <span className="text-xs font-mono" style={{ color }}>{formatCompact(value)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Company conflict banners — compact collapsible pills ─────────────────────
// Collapsed: single row of color-coded pills — minimal height impact for alignment
// Expanded: full detail inline below pills
function CompanyConflictBanners({ tripleCount, dualCount, ownershipResearchCount = 0, researchGeneralCount }) {
  const [expanded, setExpanded] = useState(false);

  const flags = [];
  if (tripleCount > 0) flags.push({
    key: "triple",
    icon: "⚠",
    label: "Triple Interest",
    color: "text-red-400",
    border: "border-red-500/40",
    bg: "bg-red-500/10",
    expandBg: "bg-red-500/[0.05]",
    expandBorder: "border-red-500/20",
    detail: `${tripleCount} equity holder${tripleCount !== 1 ? "s" : ""} also received general payments and research funding from this company. Simultaneous Stark Law, Anti-Kickback Statute, and research integrity exposure.`,
  });
  if (dualCount > 0) flags.push({
    key: "dual",
    icon: "⚑",
    label: "General & Ownership",
    color: "text-amber-400",
    border: "border-amber-500/40",
    bg: "bg-amber-500/10",
    expandBg: "bg-amber-500/[0.05]",
    expandBorder: "border-amber-500/20",
    detail: `${dualCount} equity holder${dualCount !== 1 ? "s" : ""} also received general payments from this company. Review for Stark Law and Anti-Kickback Statute exposure.`,
  });
  if (ownershipResearchCount > 0) flags.push({
    key: "ownres",
    icon: "⚑",
    label: "Ownership & Research",
    color: "text-teal-400",
    border: "border-teal-500/40",
    bg: "bg-teal-500/10",
    expandBg: "bg-teal-500/[0.05]",
    expandBorder: "border-teal-500/20",
    detail: `${ownershipResearchCount} equity holder${ownershipResearchCount !== 1 ? "s" : ""} also received research funding from this company. Review for research integrity and Anti-Kickback Statute exposure.`,
  });
  if (researchGeneralCount > 0) flags.push({
    key: "resgen",
    icon: "⚑",
    label: "General & Research",
    color: "text-blue-400",
    border: "border-blue-500/40",
    bg: "bg-blue-500/10",
    expandBg: "bg-blue-500/[0.05]",
    expandBorder: "border-blue-500/20",
    detail: `${researchGeneralCount} provider${researchGeneralCount !== 1 ? "s" : ""} received both general payments and research funding from this company.`,
  });

  if (!flags.length) return null;

  const hasTriple = flags.some((f) => f.key === "triple");
  // When triple interest exists, collapsed state shows only the triple pill.
  // All flags are always shown in the expanded detail.
  const collapsedFlags = hasTriple ? flags.filter((f) => f.key === "triple") : flags;

  return (
    <div>
      {/* Compact pill row — always visible, single line */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {collapsedFlags.map((f) => (
          <span
            key={f.key}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs ${f.color} ${f.border} ${f.bg}`}
          >
            <span>{f.icon}</span>
            <span className="font-medium">{f.label}</span>
          </span>
        ))}
        <button
          onClick={() => setExpanded((e) => !e)}
          className="text-white/25 hover:text-white/50 transition-colors text-xs ml-auto shrink-0"
          title={expanded ? "Hide details" : "Show details"}
        >
          {expanded ? "▲" : "▼"}
        </button>
      </div>

      {/* Expandable detail — shows all flags regardless of collapsed state */}
      {expanded && (
        <div className="mt-2 space-y-1.5">
          {/* When expanded with triple, show all pill labels first for scannability */}
          {hasTriple && flags.length > 1 && (
            <div className="flex items-center gap-1.5 flex-wrap pb-1.5 border-b border-white/5">
              {flags.filter((f) => f.key !== "triple").map((f) => (
                <span
                  key={f.key}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs ${f.color} ${f.border} ${f.bg}`}
                >
                  <span>{f.icon}</span>
                  <span className="font-medium">{f.label}</span>
                </span>
              ))}
            </div>
          )}
          {flags.map((f) => (
            <div
              key={f.key}
              className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${f.expandBorder} ${f.expandBg}`}
            >
              <span className={`${f.color} text-xs shrink-0 mt-0.5`}>{f.icon}</span>
              <div>
                <p className={`${f.color} font-medium text-xs`}>{f.label} Conflict</p>
                <p className="text-white/40 text-xs mt-0.5 leading-relaxed">{f.detail}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── General tab column ────────────────────────────────────────────────────────
function GeneralColumn({ company, color, selectedYear = "all", selectedPaymentType, onPaymentTypesLoaded }) {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [researchGeneralCount, setResearchGeneralCount] = useState(0);
  const [tripleCount, setTripleCount] = useState(0);
  const [ownershipGeneralCount, setOwnershipGeneralCount] = useState(0);
  const [topProviders, setTopProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [providerFetchYear, setProviderFetchYear] = useState(null);
  const [ownershipResearchCount, setOwnershipResearchCount] = useState(0);

  // ── Provider fetch — must be before any early returns (Rules of Hooks) ──────
  // Runs when year selector changes. On initial load, profile is null so we
  // skip and let the profile useEffect seed the first fetch.
  useEffect(() => {
    if (!profile) return; // profile not loaded yet — initial fetch handled below
    const yr = selectedYear === "all"
      ? (profile.years || []).filter((y) => y.totalPaid > 0).slice(-1)[0]?.year || 2025
      : parseInt(selectedYear);
    if (providerFetchYear === yr) return; // already have this year
    setProviderFetchYear(yr);
    setProvidersLoading(true);
    searchByCompany(profile.companyName || company.companyName, yr, { state: "ALL", specialty: "ALL" })
      .then((data) => setTopProviders((data?.providers || []).slice(0, 5)))
      .catch(() => setTopProviders([]))
      .finally(() => setProvidersLoading(false));
  }, [selectedYear, profile]);

  useEffect(() => {
    // Fetch research+general dual interest count
    getResearchGeneralDualInterestCountByCompany(company.companyId)
      .then((data) => setResearchGeneralCount(data.dualInterestCount || 0))
      .catch(() => {});

    // Fetch ownership holders for this company, then run company-scoped
    // payment + research checks to compute triple/dual ownership+general counts
    getOwnershipByCompanyId(company.companyId)
      .then((ownData) => {
        const npis = (ownData || []).map((o) => o.npi).filter(Boolean);
        if (!npis.length) return;
        Promise.all([
          checkPaymentsByCompanyBatch(npis, company.companyId),
          checkResearchByCompanyBatch(npis, company.companyId),
        ])
          .then(([payNpis, resNpis]) => {
            const paySet = new Set(payNpis);
            const resSet = new Set(resNpis);
            const ownerNpiSet = new Set(npis);
            const triple = [...ownerNpiSet].filter((npi) => paySet.has(npi) && resSet.has(npi)).length;
            const dual = [...ownerNpiSet].filter((npi) => paySet.has(npi)).length;
            const ownRes = [...ownerNpiSet].filter((npi) => resSet.has(npi)).length;
            setTripleCount(triple);
            setOwnershipGeneralCount(dual);
            setOwnershipResearchCount(ownRes);
          })
          .catch(() => {});
      })
      .catch(() => {});

    getCompanyProfile(company.companyId)
      .then((p) => {
        setProfile(p);
        // Report all payment types from this company to parent for dropdown
        if (onPaymentTypesLoaded && p?.years) {
          const typeSet = new Set();
          p.years.forEach((y) => {
            let types = [];
            try {
              types = Array.isArray(y.paymentTypes)
                ? y.paymentTypes
                : JSON.parse(y.paymentTypes || "[]");
            } catch {}
            types.forEach((t) => {
              const label = shortPaymentType(t.payment_type || t.paymentType || "");
              if (label) typeSet.add(label);
            });
          });
          onPaymentTypesLoaded([...typeSet]);
        }
        // Provider fetch is handled by the useEffect watching [selectedYear, profile]
        // Setting profile state will trigger that effect automatically
      })
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, [company.companyId]);

  if (loading) return (
    <div className="card flex flex-col gap-4" style={{ borderTop: `3px solid ${color}` }}>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />
        <span className="font-display text-lg text-white truncate">{company.companyName}</span>
      </div>
      <div className="text-white/30 text-sm animate-pulse">Loading…</div>
    </div>
  );

  if (!profile) return (
    <div className="card flex flex-col gap-4" style={{ borderTop: `3px solid ${color}` }}>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />
        <span className="font-display text-lg text-white truncate">{company.companyName}</span>
      </div>
      <p className="text-white/30 text-sm">No data found.</p>
    </div>
  );

  const years = profile.years || [];
  const activeYears = years.filter((y) => y.totalPaid > 0);
  const firstYear = activeYears[0]?.year;
  const latestYear = activeYears[activeYears.length - 1]?.year;
  const isAllYears = selectedYear === "all";
  const displayYear = isAllYears ? latestYear : parseInt(selectedYear);

  // isTypeFiltered and getFilteredYearTotal must be defined before any stats that use them
  const isTypeFiltered = selectedPaymentType && selectedPaymentType.size > 0;

  function getFilteredYearTotal(yd) {
    if (!yd) return 0;
    if (!isTypeFiltered) return yd.totalPaid || 0;
    let types = [];
    try {
      types = Array.isArray(yd.paymentTypes) ? yd.paymentTypes : JSON.parse(yd.paymentTypes || "[]");
    } catch {}
    return types
      .filter((t) => selectedPaymentType.has(shortPaymentType(t.payment_type || t.paymentType || "")))
      .reduce((s, t) => s + (t.amount || 0), 0);
  }

  // Stats — filtered by selected year and payment types
  const selectedYearData = years.find((y) => y.year === displayYear);
  const peakProviders = years.length ? Math.max(...years.map((y) => y.totalPhysicians || 0)) : 0;

  const allTimeTotal = isTypeFiltered
    ? years.reduce((s, y) => s + getFilteredYearTotal(y), 0)
    : years.reduce((s, y) => s + (y.totalPaid || 0), 0);

  const allTimePayments = (() => {
    if (!isTypeFiltered) return years.reduce((s, y) => s + (y.paymentCount || 0), 0);
    return years.reduce((s, yd) => {
      let types = [];
      try { types = Array.isArray(yd.paymentTypes) ? yd.paymentTypes : JSON.parse(yd.paymentTypes || "[]"); } catch {}
      return s + types
        .filter((t) => selectedPaymentType.has(shortPaymentType(t.payment_type || t.paymentType || "")))
        .reduce((ss, t) => ss + (t.count || 1), 0);
    }, 0);
  })();

  const displayTotal = isAllYears ? allTimeTotal : getFilteredYearTotal(selectedYearData);
  const displayPayments = isAllYears ? allTimePayments : (() => {
    if (!selectedYearData) return 0;
    if (!isTypeFiltered) return selectedYearData.paymentCount || 0;
    let types = [];
    try { types = Array.isArray(selectedYearData.paymentTypes) ? selectedYearData.paymentTypes : JSON.parse(selectedYearData.paymentTypes || "[]"); } catch {}
    return types
      .filter((t) => selectedPaymentType.has(shortPaymentType(t.payment_type || t.paymentType || "")))
      .reduce((s, t) => s + (t.count || 1), 0);
  })();
  const displayProviders = isAllYears ? peakProviders : (selectedYearData?.totalPhysicians || 0);

  // Payment trend data — filtered by selected types
  const trendData = ALL_YEARS.map((yr) => {
    const yd = years.find((y) => y.year === yr);
    return { year: yr, value: getFilteredYearTotal(yd) };
  });

  // Use selected year data for products/specialties/payment types
  const focusYearData = selectedYearData || years.find((y) => y.year === latestYear);
  const focusYearLabel = isAllYears ? `${latestYear}` : `${displayYear}`;

  let topSpecialties = [];
  try {
    topSpecialties = Array.isArray(focusYearData?.specialtyBreakdown)
      ? focusYearData.specialtyBreakdown
      : JSON.parse(focusYearData?.specialtyBreakdown || "[]");
    topSpecialties = topSpecialties.filter((s) => s.specialty?.trim()).sort((a, b) => b.amount - a.amount).slice(0, 5);
  } catch {}

  let topPaymentTypes = [];
  try {
    const types = Array.isArray(focusYearData?.paymentTypes)
      ? focusYearData.paymentTypes
      : JSON.parse(focusYearData?.paymentTypes || "[]");
    const typeMap = {};
    types.forEach((t) => {
      const label = shortPaymentType(t.payment_type || t.paymentType || "");
      if (label) typeMap[label] = (typeMap[label] || 0) + (t.amount || 0);
    });
    topPaymentTypes = Object.entries(typeMap).sort((a, b) => b[1] - a[1]).slice(0, 4);
  } catch {}

  return (
    <div className="card flex flex-col gap-4" style={{ borderTop: `3px solid ${color}` }}>
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />
          <Link
            to={`/company/${company.companyId}`}
            className="font-display text-lg text-white hover:text-gold-300 transition-colors truncate block"
          >
            {company.companyName}
          </Link>
        </div>
        <p className="text-white/30 text-xs ml-5 font-mono">ID: {company.companyId}</p>
        {firstYear && latestYear && (
          <p className="text-white/20 text-xs ml-5">{firstYear}–{latestYear}</p>
        )}
      </div>

      {/* Conflict banners */}
      <CompanyConflictBanners
        tripleCount={tripleCount}
        dualCount={ownershipGeneralCount}
        ownershipResearchCount={ownershipResearchCount}
        researchGeneralCount={researchGeneralCount}
      />

      {/* Key stats */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-navy-700/50 rounded-lg p-3 text-center">
          <div className="font-display text-xl" style={{ color }}>{formatCompact(displayTotal)}</div>
          <div className="text-white/30 text-xs mt-0.5">{isAllYears ? "all-time total" : `${displayYear} total`}</div>
        </div>
        <div className="bg-navy-700/50 rounded-lg p-3 text-center">
          <div className="font-display text-xl text-white">{displayProviders.toLocaleString()}</div>
          <div className="text-white/30 text-xs mt-0.5">{isAllYears ? "peak providers/yr" : `${displayYear} providers`}</div>
        </div>
        <div className="bg-navy-700/50 rounded-lg p-3 text-center">
          <div className="font-display text-lg text-white">{displayPayments.toLocaleString()}</div>
          <div className="text-white/30 text-xs mt-0.5">{isAllYears ? "total payments" : `${displayYear} payments`}</div>
        </div>
        <div className="bg-navy-700/50 rounded-lg p-3 text-center">
          <div className="font-display text-lg text-white">{activeYears.length}</div>
          <div className="text-white/30 text-xs mt-0.5">active years</div>
        </div>
      </div>

      {/* Payment trend mini chart */}
      {trendData.some((d) => d.value > 0) && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-white/40 text-xs uppercase tracking-widest">
              {!isTypeFiltered ? "Payment trend" : selectedPaymentType.size === 1 ? `${[...selectedPaymentType][0]} trend` : `Payment trend (${selectedPaymentType.size} types)`}
            </p>
            <ExportButton onClick={() => exportCsv(
              trendData.filter((d) => d.value > 0).map((d) => ({
                "Company": company.companyName,
                "Company ID": `'${company.companyId}`,
                "Year": d.year,
                "Total ($)": d.value,
                "Payment Type Filter": isTypeFiltered ? [...selectedPaymentType].join("; ") : "All",
              })),
              `${company.companyName.replace(/\s+/g, "_")}_payment_trend.csv`
            )} />
          </div>
          <MiniTrendBar data={trendData} color={color} height={96} selectedYear={selectedYear} />
          <div className="flex justify-between mt-1">
            <span className="text-white/20" style={{ fontSize: "8px" }}>2016</span>
            <span className="text-white/20" style={{ fontSize: "8px" }}>2025</span>
          </div>
        </div>
      )}

      {/* Payment types — pie chart with top 4 + Other */}
      {topPaymentTypes.length > 0 && (
        <PaymentTypePieChart
          paymentTypes={topPaymentTypes}
          color={color}
          focusYearLabel={focusYearLabel}
          focusYearData={focusYearData}
          company={company}
          isTypeFiltered={isTypeFiltered}
          selectedPaymentType={selectedPaymentType}
        />
      )}

      {/* Top providers — year-specific, linked to PhysicianPage */}
      {(topProviders.length > 0 || providersLoading) && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-white/40 text-xs uppercase tracking-widest">
              Top providers ({focusYearLabel})
            </p>
            <ExportButton onClick={() => exportCsv(
              topProviders.map((p) => ({
                "Company": company.companyName,
                "Company ID": `'${company.companyId}`,
                "Year": focusYearLabel,
                "NPI": p.npi || "",
                "First Name": p.firstName || "",
                "Last Name": p.lastName || "",
                "Specialty": p.specialty || "",
                "City": p.city || "",
                "State": p.state || "",
                "Total Payments ($)": p.totalPayments || 0,
                "Payment Count": p.paymentCount || 0,
                "Profile URL": `https://www.providerpaywatch.com/physician/${p.npi}`,
              })),
              `${company.companyName.replace(/\s+/g, "_")}_top_providers_${focusYearLabel}.csv`
            )} />
          </div>
          {providersLoading ? (
            <div className="text-white/20 text-xs animate-pulse">Loading providers…</div>
          ) : (
            <div className="space-y-2">
              {topProviders.map((p, i) => (
                <div key={p.npi || i} className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <Link
                      to={`/physician/${p.npi}?name=${encodeURIComponent(`${p.firstName || ""} ${p.lastName || ""}`.trim())}&state=${p.state || ""}`}
                      className="text-xs text-white/70 hover:text-gold-400 transition-colors truncate block max-w-36"
                    >
                      {p.firstName} {p.lastName}
                    </Link>
                    <span className="text-white/25 truncate block max-w-36" style={{ fontSize: "9px" }}>
                      {p.specialty ? p.specialty.split("|").pop().trim() : ""}
                    </span>
                  </div>
                  <span className="text-xs shrink-0 font-mono" style={{ color }}>
                    {formatCompact(p.totalPayments)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Top specialties — all included in export */}
      {topSpecialties.length > 0 && (() => {
        let allSpecialties = [];
        try {
          allSpecialties = Array.isArray(focusYearData?.specialtyBreakdown)
            ? focusYearData.specialtyBreakdown
            : JSON.parse(focusYearData?.specialtyBreakdown || "[]");
          allSpecialties = allSpecialties.filter((s) => s.specialty?.trim()).sort((a, b) => b.amount - a.amount);
        } catch {}
        return (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-white/40 text-xs uppercase tracking-widest">Top specialties ({focusYearLabel})</p>
              <ExportButton onClick={() => exportCsv(
                allSpecialties.map((s) => ({
                  "Company": company.companyName,
                  "Company ID": `'${company.companyId}`,
                  "Year": focusYearLabel,
                  "Specialty": s.specialty,
                  "Specialty (Display)": shortSpecialty(s.specialty),
                  "Provider Count": s.physician_count,
                  "Amount ($)": s.amount,
                })),
                `${company.companyName.replace(/\s+/g, "_")}_specialties_${focusYearLabel}.csv`
              )} />
            </div>
            <div className="space-y-1">
              {topSpecialties.map((s) => (
                <div key={s.specialty} className="flex justify-between items-center gap-2">
                  <span className="text-white/50 text-xs truncate min-w-0">{shortSpecialty(s.specialty)}</span>
                  <span className="text-white/30 text-xs shrink-0">{s.physician_count} providers</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      <Link
        to={`/company/${company.companyId}`}
        className="print:hidden text-xs transition-colors hover:opacity-80 block mt-auto"
        style={{ color }}
      >
        View full profile →
      </Link>
    </div>
  );
}

// ── Ownership tab column ──────────────────────────────────────────────────────
function OwnershipColumn({ company, color, loaded }) {
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [ownership, setOwnership] = useState([]);
  const [yearlyData, setYearlyData] = useState([]);
  const [companyPayNpis, setCompanyPayNpis] = useState(new Set());
  const [companyResNpis, setCompanyResNpis] = useState(new Set());
  const [researchGeneralCount, setResearchGeneralCount] = useState(0);

  useEffect(() => {
    if (!loaded || fetched) return;
    setLoading(true);
    setFetched(true);
    // Fetch research+general dual interest count for complete conflict picture
    getResearchGeneralDualInterestCountByCompany(company.companyId)
      .then((data) => setResearchGeneralCount(data.dualInterestCount || 0))
      .catch(() => {});
    Promise.all([
      getOwnershipByCompanyId(company.companyId),
      getOwnershipDetailByCompany(company.companyId),
    ])
      .then(([own, yearly]) => {
        const ownData = own || [];
        setOwnership(ownData);
        setYearlyData(yearly || []);
        // Run company-scoped conflict checks on ownership NPIs
        const npis = ownData.map((o) => o.npi).filter(Boolean);
        if (npis.length > 0) {
          Promise.all([
            checkPaymentsByCompanyBatch(npis, company.companyId),
            checkResearchByCompanyBatch(npis, company.companyId),
          ])
            .then(([payNpis, resNpis]) => {
              setCompanyPayNpis(new Set(payNpis));
              setCompanyResNpis(new Set(resNpis));
            })
            .catch(() => {});
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [loaded, company.companyId, fetched]);

  const ownerNpiSet = new Set(ownership.map((o) => o.npi).filter(Boolean));
  const ownershipResearchCount = [...ownerNpiSet].filter((npi) => companyResNpis.has(npi)).length;

  if (!loaded) return null;

  if (loading) return (
    <div className="card flex flex-col gap-4" style={{ borderTop: `3px solid ${color}` }}>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />
        <span className="font-display text-lg text-white truncate">{company.companyName}</span>
      </div>
      <div className="text-white/30 text-sm animate-pulse">Loading ownership data…</div>
    </div>
  );

  if (!ownership || ownership.length === 0) return (
    <div className="card flex flex-col gap-4" style={{ borderTop: `3px solid ${color}` }}>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />
        <Link to={`/company/${company.companyId}`} className="font-display text-lg text-white hover:text-gold-300 transition-colors truncate">{company.companyName}</Link>
      </div>
      <p className="text-white/30 text-sm">No ownership interest records found.</p>
    </div>
  );

  const activeOwnership = ownership.filter((o) => o.hasActiveOwnership);
  // currentOwnership = active holders only. No fallback — zero means zero.
  const currentOwnership = activeOwnership;
  const hasActiveHolders = activeOwnership.length > 0;

  const totalValue = currentOwnership.reduce((s, o) => s + (o.valueOfInterest || 0), 0);

  // Interest type breakdown — active holders only
  const interestTypes = {};
  currentOwnership.forEach((o) => {
    const cat = categorizeTerms(o.termsOfInterest);
    if (cat !== "Other") {
      if (!interestTypes[cat]) interestTypes[cat] = { count: 0, value: 0 };
      interestTypes[cat].count += 1;
      interestTypes[cat].value += o.valueOfInterest || 0;
    }
  });
  const sortedTypes = Object.entries(interestTypes).sort((a, b) => b[1].value - a[1].value).slice(0, 5);

  // Equity by year trend from yearlyData
  const trendData = ALL_YEARS.map((yr) => {
    const yd = yearlyData.find((d) => d.programYear === yr);
    return { year: yr, value: yd?.holderCount || 0 };
  });

  return (
    <div className="card flex flex-col gap-4" style={{ borderTop: `3px solid ${color}` }}>
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />
          <Link to={`/company/${company.companyId}`} className="font-display text-lg text-white hover:text-gold-300 transition-colors truncate block">
            {company.companyName}
          </Link>
        </div>
      </div>

      {/* Conflict banners — company-scoped */}
      {(() => {
        const ownerNpiSet = new Set(ownership.map((o) => o.npi).filter(Boolean));
        const tripleCount = [...ownerNpiSet].filter((npi) => companyPayNpis.has(npi) && companyResNpis.has(npi)).length;
        const dualCount = [...ownerNpiSet].filter((npi) => companyPayNpis.has(npi) || companyResNpis.has(npi)).length;
        return (
          <CompanyConflictBanners
            tripleCount={tripleCount}
            dualCount={dualCount}
            ownershipResearchCount={ownershipResearchCount}
            researchGeneralCount={researchGeneralCount}
          />
        );
      })()}

      {/* Key stats */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-navy-700/50 rounded-lg p-3 text-center">
          <div className="font-display text-xl" style={{ color }}>{ownership.length}</div>
          <div className="text-white/30 text-xs mt-0.5">equity holders</div>
        </div>
        <div className="bg-navy-700/50 rounded-lg p-3 text-center">
          <div className="text-green-400 font-display text-xl">{activeOwnership.length}</div>
          <div className="text-white/30 text-xs mt-0.5">active (2025)</div>
        </div>
        <div className="bg-navy-700/50 rounded-lg p-3 text-center col-span-2">
          <div className="font-display text-lg" style={{ color: hasActiveHolders ? color : "rgba(255,255,255,0.3)" }}>
            {hasActiveHolders ? formatCompact(totalValue) : "$0"}
          </div>
          <div className="text-white/30 text-xs mt-0.5">current interest value (2025)</div>
        </div>
      </div>

      {/* Ownership value by year — SVG line chart */}
      {yearlyData.some((d) => d.totalValue > 0) && (
        <OwnershipValueLineChart
          yearlyData={yearlyData}
          color={color}
          companyName={company.companyName}
          companyId={company.companyId}
        />
      )}

      {/* Equity holders by year mini chart */}
      {trendData.some((d) => d.value > 0) && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-white/40 text-xs uppercase tracking-widest">Equity holders by year</p>
            <ExportButton onClick={() => exportCsv(
              yearlyData.filter((d) => d.holderCount > 0).map((d) => ({
                "Company": company.companyName,
                "Company ID": `'${company.companyId}`,
                "Year": d.programYear,
                "Holder Count": d.holderCount,
                "Total Value ($)": d.totalValue || 0,
              })),
              `${company.companyName.replace(/\s+/g, "_")}_equity_holders_by_year.csv`
            )} />
          </div>
          <MiniTrendBar
            data={trendData}
            color={color}
            height={70}
            formatValue={(v) => v.toLocaleString()}
          />
          <div className="flex justify-between mt-1">
            <span className="text-white/20" style={{ fontSize: "8px" }}>2016</span>
            <span className="text-white/20" style={{ fontSize: "8px" }}>2025</span>
          </div>
        </div>
      )}

      {/* Top equity holders by value — latest year data, linked to PhysicianPage */}
      {hasActiveHolders && (() => {
        const topHolders = [...currentOwnership]
          .sort((a, b) => (b.valueOfInterest || 0) - (a.valueOfInterest || 0))
          .slice(0, 5);
        const allHoldersSorted = [...currentOwnership].sort((a, b) => (b.valueOfInterest || 0) - (a.valueOfInterest || 0));
        return (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-white/40 text-xs uppercase tracking-widest">Top equity holders</p>
              <ExportButton onClick={() => exportCsv(
                allHoldersSorted.map((o) => {
                  const isTriple = companyPayNpis.has(o.npi) && companyResNpis.has(o.npi);
                  const isDual = !isTriple && (companyPayNpis.has(o.npi) || companyResNpis.has(o.npi));
                  return {
                    "Company": company.companyName,
                    "Company ID": `'${company.companyId}`,
                    "NPI": o.npi,
                    "First Name": o.firstName || "",
                    "Last Name": o.lastName || "",
                    "Interest Holder": o.interestHolder || "",
                    "Terms of Interest": o.termsOfInterest || "",
                    "Value of Interest ($)": o.valueOfInterest || 0,
                    "Amount Invested ($)": o.totalAmountInvested || 0,
                    "Earliest Year": o.firstYear || "",
                    "Latest Year": o.latestYear || "",
                    "Active 2025": o.hasActiveOwnership ? "Yes" : "No",
                    "Dual Interest": isDual ? "Yes" : "No",
                    "Triple Interest": isTriple ? "Yes" : "No",
                    "Profile URL": `https://www.providerpaywatch.com/physician/${o.npi}`,
                  };
                }),
                `${company.companyName.replace(/\s+/g, "_")}_equity_holders.csv`
              )} />
            </div>
            <div className="space-y-2">
              {topHolders.map((o, idx) => {
                const name = `${o.firstName || ""} ${o.lastName || ""}`.trim() || `NPI: ${o.npi}`;
                return (
                  <div key={o.npi || idx} className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <Link
                        to={`/physician/${o.npi}?name=${encodeURIComponent(name)}`}
                        className="text-xs text-white/70 hover:text-gold-400 transition-colors truncate block max-w-36"
                      >
                        {name}
                      </Link>
                    </div>
                    <span className="text-xs shrink-0 font-mono" style={{ color }}>
                      {formatCompact(o.valueOfInterest)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Physician vs family member value split — active holders only */}
      {hasActiveHolders && (() => {
        let physicianValue = 0, familyValue = 0, jointValue = 0;
        currentOwnership.forEach((o) => {
          const h = o.interestHolder || "";
          if (h.includes("Family Member") && h.includes("Physician")) jointValue += o.valueOfInterest || 0;
          else if (h.includes("Family")) familyValue += o.valueOfInterest || 0;
          else physicianValue += o.valueOfInterest || 0;
        });
        // Only render when there is any family or joint involvement
        if (familyValue === 0 && jointValue === 0) return null;
        const total = physicianValue + familyValue + jointValue;
        if (total === 0) return null;
        const physPct = (physicianValue / total) * 100;
        const famPct = (familyValue / total) * 100;
        const jointPct = (jointValue / total) * 100;
        return (
          <div>
            <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Holder interest split</p>
            {/* Split bar */}
            <div className="flex rounded-full overflow-hidden h-2 mb-2">
              {physicianValue > 0 && <div className="bg-amber-400/80" style={{ width: `${physPct}%` }} />}
              {familyValue > 0 && <div className="bg-violet-500/70" style={{ width: `${famPct}%` }} />}
              {jointValue > 0 && <div className="bg-teal-500/70" style={{ width: `${jointPct}%` }} />}
            </div>
            <div className="space-y-1.5">
              {physicianValue > 0 && (
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-sm bg-amber-400/80 shrink-0" />
                    <span className="text-white/50 text-xs">Physician</span>
                    <span className="text-white/25 text-xs">{physPct.toFixed(0)}%</span>
                  </div>
                  <span className="text-xs font-mono text-amber-400">{formatCompact(physicianValue)}</span>
                </div>
              )}
              {familyValue > 0 && (
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-sm bg-violet-500/70 shrink-0" />
                    <span className="text-white/50 text-xs">Family Member</span>
                    <span className="text-white/25 text-xs">{famPct.toFixed(0)}%</span>
                  </div>
                  <span className="text-xs font-mono text-violet-400">{formatCompact(familyValue)}</span>
                </div>
              )}
              {jointValue > 0 && (
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-sm bg-teal-500/70 shrink-0" />
                    <span className="text-white/50 text-xs">Physician & Family</span>
                    <span className="text-white/25 text-xs">{jointPct.toFixed(0)}%</span>
                  </div>
                  <span className="text-xs font-mono text-teal-400">{formatCompact(jointValue)}</span>
                </div>
              )}
            </div>
            <p className="text-white/20 text-xs mt-2">
              ⚠ Family member interests carry the same Stark Law exposure as physician-held interests.
            </p>
          </div>
        );
      })()}

      {/* Interest types — active holders only */}
      {hasActiveHolders && sortedTypes.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-white/40 text-xs uppercase tracking-widest">Interest types</p>
            <ExportButton onClick={() => exportCsv(
              Object.entries(interestTypes).sort((a, b) => b[1].value - a[1].value).map(([cat, data]) => ({
                "Company": company.companyName,
                "Company ID": `'${company.companyId}`,
                "Interest Type": cat,
                "Holder Count": data.count,
                "Total Value ($)": data.value,
              })),
              `${company.companyName.replace(/\s+/g, "_")}_interest_types.csv`
            )} />
          </div>
          <div className="space-y-1">
            {sortedTypes.map(([cat, data]) => (
              <div key={cat} className="flex justify-between items-center gap-2">
                <span className="text-white/50 text-xs truncate min-w-0">{cat}</span>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-white/30 text-xs">{data.count}</span>
                  <span className="text-xs" style={{ color }}>{formatCompact(data.value)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!hasActiveHolders && (
        <p className="text-white/25 text-xs">
          No active ownership records as of 2025. Historical year data shown above.
        </p>
      )}

      <Link to={`/company/${company.companyId}`} className="print:hidden text-xs transition-colors hover:opacity-80 block mt-auto" style={{ color }}>
        View full ownership →
      </Link>
    </div>
  );
}

// ── Research tab column ───────────────────────────────────────────────────────
function ResearchColumn({ company, color, loaded }) {
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [research, setResearch] = useState([]);
  const [yearlyData, setYearlyData] = useState([]);
  const [productSummary, setProductSummary] = useState([]);
  const [researchGeneralCount, setResearchGeneralCount] = useState(0);
  const [tripleCount, setTripleCount] = useState(0);
  const [ownershipGeneralCount, setOwnershipGeneralCount] = useState(0);
  const [ownershipResearchCount, setOwnershipResearchCount] = useState(0);

  useEffect(() => {
    if (!loaded || fetched) return;
    setLoading(true);
    setFetched(true);

    // Fetch research+general dual interest count
    getResearchGeneralDualInterestCountByCompany(company.companyId)
      .then((data) => setResearchGeneralCount(data.dualInterestCount || 0))
      .catch(() => {});

    // Fetch ownership NPIs for company-scoped triple/dual conflict detection
    getOwnershipByCompanyId(company.companyId)
      .then((ownData) => {
        const npis = (ownData || []).map((o) => o.npi).filter(Boolean);
        if (!npis.length) return;
        Promise.all([
          checkPaymentsByCompanyBatch(npis, company.companyId),
          checkResearchByCompanyBatch(npis, company.companyId),
        ])
          .then(([payNpis, resNpis]) => {
            const paySet = new Set(payNpis);
            const resSet = new Set(resNpis);
            const ownerNpiSet = new Set(npis);
            const triple = [...ownerNpiSet].filter((npi) => paySet.has(npi) && resSet.has(npi)).length;
            const dual = [...ownerNpiSet].filter((npi) => paySet.has(npi)).length;
            const ownRes = [...ownerNpiSet].filter((npi) => resSet.has(npi)).length;
            setTripleCount(triple);
            setOwnershipGeneralCount(dual);
            setOwnershipResearchCount(ownRes);
          })
          .catch(() => {});
      })
      .catch(() => {});

    Promise.all([
      getResearchByCompany(company.companyId),
      getResearchByCompanyByYear(company.companyId),
      getResearchProductSummaryByCompany(company.companyId),
    ])
      .then(([res, yearly, products]) => {
        setResearch(res || []);
        setYearlyData(yearly || []);
        setProductSummary(products?.products || products || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [loaded, company.companyId, fetched]);

  if (!loaded) return null;

  if (loading) return (
    <div className="card flex flex-col gap-4" style={{ borderTop: `3px solid ${color}` }}>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />
        <span className="font-display text-lg text-white truncate">{company.companyName}</span>
      </div>
      <div className="text-white/30 text-sm animate-pulse">Loading research data…</div>
    </div>
  );

  if (!research || research.length === 0) return (
    <div className="card flex flex-col gap-4" style={{ borderTop: `3px solid ${color}` }}>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />
        <Link to={`/company/${company.companyId}`} className="font-display text-lg text-white hover:text-gold-300 transition-colors truncate">{company.companyName}</Link>
      </div>
      <p className="text-white/30 text-sm">No research payment records found.</p>
    </div>
  );

  const totalFunding = yearlyData.reduce((s, y) => s + (y.totalAmount || 0), 0);
  const totalInvestigators = research.length;
  const totalStudies = yearlyData.reduce((s, y) => s + (y.studyCount || 0), 0);
  const activeCount = yearlyData.find((y) => y.programYear === 2025)?.investigatorCount || 0;

  // Preclinical vs clinical split — using hasPreclinical flag on research summary rows
  const preclinicalInvestigators = research.filter((r) => r.hasPreclinical);
  const preclinicalFunding = preclinicalInvestigators.reduce((s, r) => s + (r.totalAmount || 0), 0);
  const clinicalFunding = totalFunding - preclinicalFunding;
  const preclinicalPct = totalFunding > 0 ? Math.round((preclinicalFunding / totalFunding) * 100) : 0;
  const clinicalPct = 100 - preclinicalPct;
  const hasPreclinical = preclinicalInvestigators.length > 0;

  // Funding trend
  const trendData = ALL_YEARS.map((yr) => {
    const yd = yearlyData.find((d) => d.programYear === yr);
    return { year: yr, value: yd?.totalAmount || 0 };
  });

  // Product type breakdown
  const typeMap = {};
  productSummary.forEach((p) => {
    const label = p.productType || "Not Specified";
    if (!typeMap[label]) typeMap[label] = 0;
    typeMap[label] += p.totalAmount || 0;
  });
  const productTypes = Object.entries(typeMap)
    .filter(([l]) => l !== "Not Specified")
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4);

  // Top products by funding (named only)
  const namedProducts = productSummary
    .filter((p) => p.productName?.trim())
    .sort((a, b) => (b.totalAmount || 0) - (a.totalAmount || 0))
    .slice(0, 5);

  // Top investigators
  const topInvestigators = research.slice(0, 5);

  return (
    <div className="card flex flex-col gap-4" style={{ borderTop: `3px solid ${color}` }}>
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />
          <Link to={`/company/${company.companyId}`} className="font-display text-lg text-white hover:text-gold-300 transition-colors truncate block">
            {company.companyName}
          </Link>
        </div>
      </div>

      {/* Conflict banner */}
      <CompanyConflictBanners
        tripleCount={tripleCount}
        dualCount={ownershipGeneralCount}
        ownershipResearchCount={ownershipResearchCount}
        researchGeneralCount={researchGeneralCount}
      />

      {/* Key stats */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-navy-700/50 rounded-lg p-3 text-center">
          <div className="font-display text-xl" style={{ color }}>{formatCompact(totalFunding)}</div>
          <div className="text-white/30 text-xs mt-0.5">total funding</div>
        </div>
        <div className="bg-navy-700/50 rounded-lg p-3 text-center">
          <div className="font-display text-xl text-white">{totalInvestigators.toLocaleString()}</div>
          <div className="text-white/30 text-xs mt-0.5">investigators</div>
        </div>
        <div className="bg-navy-700/50 rounded-lg p-3 text-center">
          <div className="font-display text-lg text-white">{totalStudies.toLocaleString()}</div>
          <div className="text-white/30 text-xs mt-0.5">studies</div>
        </div>
        <div className="bg-navy-700/50 rounded-lg p-3 text-center">
          <div className="text-green-400 font-display text-lg">{activeCount}</div>
          <div className="text-white/30 text-xs mt-0.5">active (2025)</div>
        </div>
      </div>

      {/* Research funding trend mini chart */}
      {trendData.some((d) => d.value > 0) && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-white/40 text-xs uppercase tracking-widest">Funding trend</p>
            <ExportButton onClick={() => exportCsv(
              trendData.filter((d) => d.value > 0).map((d) => {
                const yd = yearlyData.find((y) => y.programYear === d.year);
                return {
                  "Company": company.companyName,
                  "Company ID": `'${company.companyId}`,
                  "Year": d.year,
                  "Total Funding ($)": d.value,
                  "Investigators": yd?.investigatorCount || "",
                  "Payments": yd?.paymentCount || "",
                  "Studies": yd?.studyCount || "",
                };
              }),
              `${company.companyName.replace(/\s+/g, "_")}_research_funding_trend.csv`
            )} />
          </div>
          <MiniTrendBar data={trendData} color={color} height={96} />
          <div className="flex justify-between mt-1">
            <span className="text-white/20" style={{ fontSize: "8px" }}>2016</span>
            <span className="text-white/20" style={{ fontSize: "8px" }}>2025</span>
          </div>
        </div>
      )}

      {/* Preclinical vs clinical split — mirrors ComparePage ResearchColumn */}
      {hasPreclinical && (
        <div>
          <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Preclinical vs clinical</p>
          <div className="space-y-2">
            <div className="flex rounded-full overflow-hidden h-2">
              <div className="bg-teal-500/70" style={{ width: `${clinicalPct}%` }} />
              <div className="bg-violet-500/70" style={{ width: `${preclinicalPct}%` }} />
            </div>
            <div className="flex justify-between text-xs">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-sm bg-teal-500/70 shrink-0" />
                <span className="text-white/50">Clinical {clinicalPct}%</span>
                <span className="text-white/30">{formatCompact(clinicalFunding)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-sm bg-violet-500/70 shrink-0" />
                <span className="text-white/50">Preclinical {preclinicalPct}%</span>
                <span className="text-violet-400">{formatCompact(preclinicalFunding)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Product type breakdown */}
      {productTypes.length > 0 && (
        <div>
          <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Product types</p>
          <div className="space-y-1">
            {productTypes.map(([label, amount]) => (
              <div key={label} className="flex justify-between items-center gap-2">
                <span className="text-white/50 text-xs truncate min-w-0">{label}</span>
                <span className="text-xs shrink-0" style={{ color }}>{formatCompact(amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top products by funding — all in export */}
      {namedProducts.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-white/40 text-xs uppercase tracking-widest">Top products</p>
            <ExportButton onClick={() => exportCsv(
              productSummary
                .filter((p) => p.productName?.trim())
                .sort((a, b) => (b.totalAmount || 0) - (a.totalAmount || 0))
                .map((p) => ({
                  "Company": company.companyName,
                  "Company ID": `'${company.companyId}`,
                  "Product Name": p.productName,
                  "Product Type": p.productType || "",
                  "Total Funding ($)": p.totalAmount || 0,
                  "Investigator Count": p.investigatorCount || "",
                  "Study Count": p.studyCount || "",
                })),
              `${company.companyName.replace(/\s+/g, "_")}_research_products.csv`
            )} />
          </div>
          <div className="space-y-1">
            {namedProducts.map((p) => (
              <div key={p.productName} className="flex justify-between items-center gap-2">
                <span className="text-white/50 text-xs truncate min-w-0">{p.productName}</span>
                <span className="text-xs shrink-0" style={{ color }}>{formatCompact(p.totalAmount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top investigators — all in export with last active year */}
      {topInvestigators.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-white/40 text-xs uppercase tracking-widest">Top investigators</p>
            <ExportButton onClick={() => exportCsv(
              research.map((r) => ({
                "NPI": r.npi,
                "First Name": r.firstName || "",
                "Last Name": r.lastName || "",
                "Specialty": r.specialty || "",
                "State": r.state || "",
                "Total Funding ($)": r.totalAmount || 0,
                "Payment Count": r.paymentCount || 0,
                "Study Count": r.studyCount || 0,
                "First Year": r.firstYear || "",
                "Last Active Year": r.latestYear || "",
                "Active 2025": r.hasActiveResearch ? "Yes" : "No",
                "Profile URL": `https://www.providerpaywatch.com/physician/${r.npi}?name=${encodeURIComponent(`${r.firstName || ""} ${r.lastName || ""}`.trim())}&state=${r.state || ""}`,
              })),
              `${company.companyName.replace(/\s+/g, "_")}_investigators.csv`
            )} />
          </div>
          <div className="space-y-1.5">
            {topInvestigators.map((r, i) => (
              <div key={i} className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <Link
                    to={`/physician/${r.npi}?name=${encodeURIComponent(`${r.firstName || ""} ${r.lastName || ""}`.trim())}&state=${r.state || ""}`}
                    className="text-xs text-white/70 hover:text-gold-400 transition-colors truncate block max-w-28"
                  >
                    {r.firstName} {r.lastName}
                  </Link>
                  <span className="text-white/25" style={{ fontSize: "9px" }}>
                    {r.hasActiveResearch ? "Active 2025" : r.latestYear ? `Last active ${r.latestYear}` : ""}
                  </span>
                </div>
                <span className="text-xs shrink-0" style={{ color }}>{formatCompact(r.totalAmount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Link to={`/company/${company.companyId}`} className="print:hidden text-xs transition-colors hover:opacity-80 block mt-auto" style={{ color }}>
        View full research →
      </Link>
    </div>
  );
}

// ── Payment type multi-select dropdown ────────────────────────────────────────
// selected = Set of selected type strings. Empty Set = all types shown.
function PaymentTypeDropdown({ paymentTypes, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function toggle(type) {
    const next = new Set(selected);
    if (next.has(type)) next.delete(type);
    else next.add(type);
    onChange(next);
  }

  function selectAll() { onChange(new Set()); }

  const count = selected.size;
  const label = count === 0 ? "All payment types"
    : count === 1 ? [...selected][0].length > 22 ? [...selected][0].slice(0, 22) + "…" : [...selected][0]
    : `${count} types selected`;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 border rounded-lg px-3 py-2 text-xs hover:border-white/30 transition-colors min-w-40 ${
          count > 0 ? "bg-gold-500/10 border-gold-500/30 text-gold-400" : "bg-white/5 border-white/15 text-white/70"
        }`}
      >
        <span className="flex-1 text-left">{label}</span>
        <span className="text-white/40">▾</span>
      </button>
      {open && (
        <div
          className="absolute top-full left-0 mt-1 border border-white/15 rounded-lg shadow-xl z-30 overflow-hidden min-w-56"
          style={{ background: "#0f172a" }}
        >
          <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between">
            <p className="text-white/30 text-xs">Filter payment types</p>
            {count > 0 && (
              <button onClick={selectAll} className="text-white/40 hover:text-white/70 text-xs transition-colors">
                Clear all
              </button>
            )}
          </div>
          <div className="max-h-64 overflow-y-auto">
            {paymentTypes.map((t) => {
              const isSelected = selected.has(t);
              return (
                <button
                  key={t}
                  onClick={() => toggle(t)}
                  className={`w-full text-left px-3 py-2 text-xs transition-colors flex items-center gap-2 hover:bg-white/5 ${isSelected ? "text-gold-400" : "text-white/70"}`}
                >
                  <span className={`w-3 h-3 rounded-sm border shrink-0 flex items-center justify-center ${isSelected ? "bg-gold-500 border-gold-500" : "border-white/30"}`}>
                    {isSelected && <span className="text-black text-xs leading-none">✓</span>}
                  </span>
                  <span className="truncate">{t}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Year dropdown ─────────────────────────────────────────────────────────────
function YearDropdown({ selectedYear, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const yearOptions = ["all", ...ALL_YEARS.slice().reverse()];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white text-sm hover:border-white/30 transition-colors min-w-28"
      >
        <span className="flex-1 text-left">{selectedYear === "all" ? "All Years" : selectedYear}</span>
        <span className="text-white/40">▾</span>
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 border border-white/15 rounded-lg shadow-xl z-30 overflow-hidden min-w-28" style={{ background: "#0f172a" }}>
          <div className="max-h-64 overflow-y-auto">
            {yearOptions.map((y) => (
              <button
                key={y}
                onClick={() => { onChange(String(y)); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-white/5 ${selectedYear === String(y) ? "text-gold-400 bg-gold-500/5" : "text-white/70"}`}
              >
                {y === "all" ? "All Years" : y}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function CompanyComparePage() {
  const [searchParams] = useSearchParams();
  const { isPro, setShowUpgradeModal, setUpgradeMessage } = useSubscription();
  const [activeTab, setActiveTab] = useState("general");
  const [loadedTabs, setLoadedTabs] = useState({ general: true, ownership: false, research: false });
  const [selectedYear, setSelectedYear] = useState("all");
  const [selectedPaymentType, setSelectedPaymentType] = useState(new Set());
  const [allPaymentTypes, setAllPaymentTypes] = useState([]);
  const [copied, setCopied] = useState(false);

  const companyIdParam = searchParams.get("companyIds") || "";
  const companyNameParam = searchParams.get("companyNames") || "";

  const companyIds = companyIdParam.split(",").filter(Boolean).map(decodeURIComponent);
  // Names use "|" delimiter to avoid splitting on commas within company names (e.g. "SPR Therapeutics, Inc")
  const companyNames = decodeURIComponent(companyNameParam).split("|").filter(Boolean);

  const companies = companyIds.map((id, i) => ({
    companyId: id,
    companyName: companyNames[i] || id,
  }));

  function handleTabClick(tabId) {
    setActiveTab(tabId);
    setLoadedTabs((prev) => ({ ...prev, [tabId]: true }));
  }

  function shareComparison() {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (!isPro) {
    return (
      <main className="max-w-5xl mx-auto px-4 py-10">
        <p className="text-white/40 text-sm">Company comparison is a Pro feature.</p>
        <button
          onClick={() => {
            setUpgradeMessage("Compare up to 3 companies side by side with Pro.");
            setShowUpgradeModal(true);
          }}
          className="btn-primary mt-4"
        >
          Upgrade to Pro
        </button>
      </main>
    );
  }

  if (companies.length < 2) {
    return (
      <main className="max-w-5xl mx-auto px-4 py-10">
        <p className="text-white/40 text-sm">Select 2 or 3 companies to compare. Use the + Compare button on company pages.</p>
        <Link to="/" className="text-gold-400 text-sm mt-2 block">← Back to search</Link>
      </main>
    );
  }

  return (
    <main className="max-w-5xl mx-auto px-4 py-10">
      {/* ── Print styles ──────────────────────────────────────────────────── */}
      <style>{`
        @media print {
          @page { margin: 0.75in; size: ${companies.length === 3 ? "letter landscape" : "letter"}; }
          body {
            background: white !important;
            color: #111 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          nav, header, footer, .print-hide { display: none !important; }
          main { background: white !important; max-width: 100% !important; padding: 0 !important; }
          .card {
            background: white !important;
            border: 1px solid #e5e7eb !important;
            border-radius: 8px !important;
            box-shadow: none !important;
            break-inside: avoid;
          }
          .text-white { color: #111 !important; }
          .text-white\\/80, .text-white\\/70, .text-white\\/60, .text-white\\/50 { color: #374151 !important; }
          .text-white\\/40, .text-white\\/30, .text-white\\/25, .text-white\\/20 { color: #6b7280 !important; }
          .text-gold-400 { color: #b45309 !important; }
          .text-amber-400 { color: #b45309 !important; }
          .text-teal-400 { color: #0f766e !important; }
          .text-red-400 { color: #dc2626 !important; }
          .text-orange-400 { color: #ea580c !important; }
          .text-green-400 { color: #16a34a !important; }
          .text-blue-400 { color: #2563eb !important; }
          .text-violet-400 { color: #7c3aed !important; }
          svg text { fill: #374151 !important; }
          polyline[stroke] { stroke-opacity: 1 !important; }
          circle[fill] { fill-opacity: 1 !important; }
          path[fill] { fill-opacity: 1 !important; }

          /* Force bar/line colors */
          div[style] { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          div[style*="background-color: rgba(251,191,36"] { background-color: #d97706 !important; opacity: 1 !important; }
          div[style*="background-color: rgba(139,92,246"] { background-color: #7c3aed !important; opacity: 1 !important; }
          div[style*="background-color: rgba(20,184,166"] { background-color: #0f766e !important; opacity: 1 !important; }
          div[style*="background-color: rgba(34,197,94"]  { background-color: #16a34a !important; opacity: 1 !important; }
          div[style*="background-color: rgba(59,130,246"] { background-color: #2563eb !important; opacity: 1 !important; }
          div[style*="background-color: rgba(239,68,68"]  { background-color: #dc2626 !important; opacity: 1 !important; }
          div[style*="background-color: rgba(249,115,22"] { background-color: #ea580c !important; opacity: 1 !important; }
          div[style*="background-color: rgba(236,72,153"] { background-color: #db2777 !important; opacity: 1 !important; }
          div[style*="rgba(255,255,255,0.04"] { display: none !important; }
          div[style*="rgba(255,255,255,0.05"] { display: none !important; }

          /* Banner and stat backgrounds */
          .bg-red-500\\/10, .bg-red-500\\/5 { background: #fef2f2 !important; border-color: #fca5a5 !important; }
          .bg-amber-500\\/10, .bg-amber-500\\/5 { background: #fffbeb !important; border-color: #fcd34d !important; }
          .bg-teal-500\\/10, .bg-teal-500\\/5 { background: #f0fdfa !important; border-color: #5eead4 !important; }
          .bg-blue-500\\/10, .bg-blue-500\\/5 { background: #eff6ff !important; border-color: #93c5fd !important; }
          .bg-orange-500\\/10 { background: #fff7ed !important; border-color: #fdba74 !important; }
          .bg-violet-500\\/10 { background: #f5f3ff !important; border-color: #c4b5fd !important; }
          .bg-navy-700\\/50 { background: #f9fafb !important; border: 1px solid #e5e7eb !important; }

          /* Column grid */
          .compare-grid {
            display: grid !important;
            grid-template-columns: repeat(${companies.length}, 1fr) !important;
            gap: 12px !important;
          }

          /* Hide interactive controls */
          select { display: none !important; }
          button { display: none !important; }
        }
      `}</style>

      {/* ── Print-only report header ───────────────────────────────────────── */}
      <div className="hidden print:block mb-4 pb-3 border-b border-gray-200">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">
              ProviderPayWatch · Company Comparison Report
            </p>
            <h1 className="text-2xl font-bold text-gray-900">
              {companies.map((c) => c.companyName).join(" vs. ")}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {companies.length} companies · {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} tab
              {selectedYear !== "all" ? ` · ${selectedYear}` : " · All years"}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400">Generated {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
            <p className="text-xs text-gray-400 mt-0.5">providerpaywatch.com</p>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="print:hidden flex items-center justify-between mb-6">
        <div>
          <Link to="/" className="text-white/40 hover:text-white/70 text-sm transition-colors">← Home</Link>
          <h1 className="font-display text-3xl text-white mt-2">
            Company <span className="text-gold-400">comparison</span>
          </h1>
          <p className="text-white/40 text-sm mt-1">Comparing {companies.length} companies</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={shareComparison}
            className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border transition-all ${
              copied
                ? "text-green-400 border-green-500/30 bg-green-500/10"
                : "text-white/60 border-white/20 hover:text-white hover:border-white/40 bg-white/5"
            }`}
          >
            {copied ? "✓ Link copied!" : "⎘ Share comparison"}
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-white/20 text-white/60 hover:text-white hover:border-white/40 bg-white/5 transition-all"
          >
            <span>⎙</span><span>Print / Save PDF</span>
          </button>
        </div>
      </div>

      {/* Year selector + payment type filter — General tab only */}
      {activeTab === "general" && (
        <div className="print:hidden flex items-center gap-3 mb-4 flex-wrap">
          <span className="text-white/40 text-xs uppercase tracking-widest">Showing</span>
          <YearDropdown selectedYear={selectedYear} onChange={setSelectedYear} />
          {allPaymentTypes.length > 0 && (
            <PaymentTypeDropdown
              paymentTypes={allPaymentTypes}
              selected={selectedPaymentType}
              onChange={setSelectedPaymentType}
            />
          )}
          {selectedPaymentType.size > 0 && (
            <button
              onClick={() => setSelectedPaymentType(new Set())}
              className="text-xs text-white/30 hover:text-white/60 transition-colors"
            >
              ✕ Clear all
            </button>
          )}
        </div>
      )}

      {/* Tab bar */}
      <div className="print:hidden flex gap-1 mb-5 border-b border-white/10">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabClick(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? "text-gold-400 border-gold-400"
                : "text-white/40 border-transparent hover:text-white/70"
            }`}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab hint */}
      <p className="print:hidden text-white/20 text-xs mb-4">
        {activeTab === "general" && "General payment data from CMS Open Payments · 2016–2025"}
        {activeTab === "ownership" && "Ownership & investment interest data from CMS Open Payments · 2016–2025"}
        {activeTab === "research" && "Research payment data from CMS Open Payments · 2016–2025"}
      </p>

      {/* Columns */}
      <div className="print:overflow-visible overflow-x-auto -mx-4 px-4">
        <div
          className="grid gap-4 compare-grid"
          style={{
            gridTemplateColumns: `repeat(${companies.length}, minmax(280px, 1fr))`,
            minWidth: companies.length > 1 ? `${companies.length * 290}px` : "auto",
          }}
        >
          {companies.map((company, i) => {
            if (activeTab === "general") return (
              <GeneralColumn
                key={`${company.companyId}-${selectedYear}`}
                company={company}
                color={CHART_COLORS[i]}
                selectedYear={selectedYear}
                selectedPaymentType={selectedPaymentType}
                onPaymentTypesLoaded={(types) => {
                  // Accumulate union of payment types across all companies
                  setAllPaymentTypes((prev) => {
                    const combined = new Set([...prev, ...types]);
                    return [...combined].sort();
                  });
                }}
              />
            );
            if (activeTab === "ownership") return (
              <OwnershipColumn key={company.companyId} company={company} color={CHART_COLORS[i]} loaded={loadedTabs.ownership} />
            );
            if (activeTab === "research") return (
              <ResearchColumn key={company.companyId} company={company} color={CHART_COLORS[i]} loaded={loadedTabs.research} />
            );
            return null;
          })}
        </div>
      </div>

      <p className="print:hidden text-white/20 text-xs text-center mt-8">
        Source: CMS Open Payments · Data reported by applicable manufacturers and GPOs under the Physician Payments Sunshine Act · 2016–2025
      </p>
      <p className="hidden print:block text-gray-400 text-xs text-center mt-8">
        Source: CMS Open Payments (2016–2025) · Data reported by applicable manufacturers and GPOs as required by the Physician Payments Sunshine Act · This report supports compliance research and risk review — consult qualified legal or compliance counsel for final determinations · providerpaywatch.com · Generated {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
      </p>
    </main>
  );
}
